import { Injectable } from '@angular/core';
import {
  DataService,
  IPersistable,
  PersistenceService,
  PersistMode,
  QueryColumn,
  QueryObjectsModel,
} from '@barthauer/abc/core/data';
import { map } from 'rxjs/operators';
import { CommentModel } from '../models/comment.model';

@Injectable({
  providedIn: 'root',
})
export class CommentsService {
  private commentObjectTypeName = 'Comment';
  private persistenceCategory = 'CommentsService';
  public persistPendingChanges: (category: string) => void;

  constructor(private dataService: DataService, private persistenceService: PersistenceService) {
    this.persistPendingChanges = (category) => {
      this.persistenceService.persistPendingChanges(category);
    };
  }

  public async queryComments(
    parentId: string,
    parentObjectType: string,
    oPathFilter?: string
  ): Promise<CommentModel[]> {
    const queryColumns = this.getCommentQueryColumns();

    let queryOpath: string;
    if (parentId && oPathFilter) {
      queryOpath = 'ParentId=' + "'" + parentId + "' And " + oPathFilter;
    } else if (!oPathFilter) {
      queryOpath = 'ParentId=' + "'" + parentId + "'";
    } else if (!parentId) {
      queryOpath = oPathFilter;
    }
    const commentQuery: QueryObjectsModel = this.dataService.createSimpleQuery(
      '',
      this.commentObjectTypeName,
      queryOpath,
      queryColumns
    );

    return await this.dataService
      .executeReadObjectsQuery(commentQuery)
      .pipe(
        map((data: ArrayBuffer) => {
          // return ConvertHelper.arrayBufferToObjectArray<CommentModel>(data);
          return this.arrayBufferToObjectArray<CommentModel>(data);
        })
      )
      .toPromise();
  }

  private getCommentQueryColumns(): Array<QueryColumn> {
    return CommentModel.createQueryColumns();
  }

  //TODO: Replace with real arrayBufferToObjectArray when we know where it is
  private arrayBufferToObjectArray<T>(results: ArrayBuffer): T[] {
    if (results && Array.isArray(results)) {
      return results;
    } else {
      return [];
    }
  }

  public async getNextSeqNumber(parentObjectType: string, parentId: string, identifer: string): Promise<number> {
    const queryColumns = [
      {
        OPath: `CaseNull(Max(Comments[Identifier='${identifer}'].SeqNr), 0) + 1`,
        Name: 'NextSeqNumber',
      },
    ];

    const commentQuery: QueryObjectsModel = this.dataService.createSimpleQuery(
      '',
      parentObjectType,
      `Id = '${parentId}'`,
      queryColumns
    );

    const objectWithNextSeqNumber = await this.dataService.executeReadObjectsQuery(commentQuery).toPromise();

    return objectWithNextSeqNumber[0].NextSeqNumber;
  }

  public deleteComment(comment: CommentModel): void {
    const objectKey: IPersistable = {
      id: comment.Id,
      objectType: this.commentObjectTypeName,
      persistenceCategory: this.persistenceCategory,
      changesPersisted: (persistMode: PersistMode) => {
        persistMode = PersistMode.Delete;
      },
      revertChanges: () => {},
    };

    this.persistenceService.addObjectForDelete(objectKey);
    this.persistPendingChanges(this.persistenceCategory);
  }

  public saveComment(comment: CommentModel, parentObjectType: string, _persistMode: PersistMode): void {
    const objectKey: IPersistable = {
      id: comment.Id,
      objectType: this.commentObjectTypeName,
      persistenceCategory: this.persistenceCategory,
      changesPersisted: (persistMode: PersistMode) => (persistMode = _persistMode),
      revertChanges: () => {},
    };
    this.persistenceService.clearPendingObjects(this.persistenceCategory);

    if (_persistMode === PersistMode.Insert) {
      this.persistenceService.addObjectForInsert(objectKey);
    } else if (_persistMode === PersistMode.Merge) {
      this.persistenceService.addObjectForMerge(objectKey);
    }

    this.persistenceService.addObjectValue(objectKey, 'ParentId', comment.ParentId);
    this.persistenceService.addObjectValue(objectKey, 'ParentObjectType', parentObjectType);
    this.persistenceService.addObjectValue(objectKey, 'Identifier', comment.Identifier);
    this.persistenceService.addObjectValue(objectKey, 'SeqNr', comment.SeqNr);
    this.persistenceService.addObjectValue(objectKey, 'Text', comment.Text);
    this.persistenceService.addObjectValue(objectKey, 'Comment', comment.Comment);

    this.persistPendingChanges(this.persistenceCategory);
  }
}
